import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import {
    selectCurrentEntityType,
    selectDeleteCurrentTenantId,
    setCurrentEntityType,
} from 'common/components/deleteDialog/deleteDialogSlice';
import { useDeleteDialogHandler } from 'common/hooks/useDeleteDialogHandler';
import { deleteUserProfileUserGroup } from 'common/services/api/UserProfileUserGroups/UserProfileUserGroups.thunk';
import { DeleteDialogContentProps, DrawerEntitiesNames } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { getUserProfileTenantUpdatePayload, updateUserProfileTenant } from 'features/userProfileApps/helpers';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

export function UserProfileUserGroupDeleteDialog({
    tenant,
    itemId,
    customDeleteTitle,
    editingRow,
}: Readonly<DeleteDialogContentProps<{
    id: string,
    name: string,
    tenantId: string
}>>) {
    const { id, name } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const currentEntityType = useAppSelector(selectCurrentEntityType);
    const currentTenantId = useAppSelector(selectDeleteCurrentTenantId);
    const userDetails = useAppSelector(selectUserDetails);
    const { firstName, lastName, userId } = userDetails ?? {};

    const { processing, handleDelete } = useDeleteDialogHandler({
        deleteThunk: deleteUserProfileUserGroup,
        entityName: 'User from User Group',
        successMessage: 'The User has been removed from this User Group under this Tenant.',
        onSuccess: () => {
            dispatch(setCurrentEntityType());
            const updateUserProfileTenantPayload = getUserProfileTenantUpdatePayload(tenant, userDetails);
            updateUserProfileTenant(updateUserProfileTenantPayload);
        },
        onError: () => {
            enqueueSnackbar(
                'There was an error while removing this User from this User Group.' +
                DEFAULT_ERROR_MESSAGE,
                { variant: 'error' }
            );
            return true;
        },
    });

    const deleteCallback = () => {
        if (itemId) {
            handleDelete({
                tenantId: tenant?.id ?? '',
                userGroupId: id ?? '',
                userId: userId ?? '',
            });
        }
    };

    if (currentEntityType !== DrawerEntitiesNames.UserProfileUserGroup || currentTenantId !== tenant?.id) {
        return null;
    }

    return  (
        <DeleteDialog deleteCallback={deleteCallback} processing={processing} customDeleteTitle={customDeleteTitle}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Name: <Typography component="span" color="text.primary" fontWeight="400">{`${firstName} ${lastName}`}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                User Group Name: <Typography component="span" color="text.primary" fontWeight="400">{name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{tenant?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
